import { createRouter, createWebHistory } from 'vue-router'

import { useAppStore } from '@/stores/app'

declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?: boolean
        // Admins only (README decision 321). The menu hides the entry and the
        // guard sends anybody else to the fleet; the server answers 403.
        adminOnly?: boolean
        layout?: string
    }
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path: '/',
            redirect: '/fleet',
        },
        {
            // The sign-in page (README decision 328): one button to Imprion ID,
            // back to `redirect` afterwards. Nothing leaves for Imprion ID
            // without that click. Signed in already: straight to `redirect`.
            path:      '/login',
            name:      'login',
            component: () => import('@/views/auth/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
            beforeEnter: async (to) => {
                const appStore = useAppStore()

                await appStore.loadSession()

                return appStore.isLoggedIn() ? (safeRedirect(to.query.redirect) ?? '/fleet') : true
            },
        },
        {
            // Where the API sends a refused sign-in, with ?reason=.
            path:      '/access-denied',
            name:      'access-denied',
            component: () => import('@/views/auth/AccessDenied.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // The fleet dashboard: health, last-seen, versions, pending updates,
            // printers, filtered by client/site/model/wave (Doc 2 §5).
            path:      '/fleet',
            name:      'fleet',
            component: () => import('@/views/device/Devices.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/fleet/:deviceId',
            name:      'device',
            component: () => import('@/views/device/DevicePage.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The client registry is the claim's only required input
            // (Doc 2 §3.2), which is why it lands in M2 rather than with the
            // rest of the portal.
            path:      '/clients',
            name:      'clients',
            component: () => import('@/views/client/Clients.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/clients/:clientId',
            name:      'client',
            component: () => import('@/views/client/Client.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Wave assignment, version convergence, the soak and its halt
            // (Doc 2 §5, Doc 3 §5-§6), and the releases that are switched onto
            // the canary and stable suites: one page for what lands where.
            path:      '/rollout',
            name:      'rollout',
            component: () => import('@/views/rollout/Rollout.vue'),
            meta:      { requiresAuth: true, adminOnly: true },
        },
        {
            // Alerts are routed, not dashboard-only (Doc 2 §5): this page is
            // the record, the webhook is the delivery.
            path:      '/alerts',
            name:      'alerts',
            component: () => import('@/views/alert/Alerts.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Every action, per device, per operator. The log has no TTL: it
            // is the record of who did what to which till.
            path:      '/audit',
            name:      'audit',
            component: () => import('@/views/audit/Audits.vue'),
            meta:      { requiresAuth: true, adminOnly: true },
        },
        {
            // The technician key set is fleet-wide and this list is its source
            // of truth: removing a technician is one action here and the fleet
            // converges on the next heartbeat (D33, Doc 2 §4.4).
            path:      '/ssh-keys',
            name:      'ssh-keys',
            component: () => import('@/views/sshkey/SSHKeys.vue'),
            meta:      { requiresAuth: true, adminOnly: true },
        },
        {
            // The fleet-wide printer registry and its inbox (F90, README
            // decision 223). A printer whose USB interface is not class 7 can
            // be discovered by nothing a terminal runs, so a model is
            // registered once here rather than added by hand on fifty tills.
            path:      '/printers',
            name:      'printers',
            component: () => import('@/views/printer/Printers.vue'),
            meta:      { requiresAuth: true, adminOnly: true },
        },
        {
            // Portal accounts. Doc 2 §5 wants every action attributed to an
            // operator, which only means anything if the operator set can be
            // maintained from the portal rather than from a mongo shell.
            path:      '/operators',
            name:      'operators',
            component: () => import('@/views/operator/Operators.vue'),
            meta:      { requiresAuth: true, adminOnly: true },
        },
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/Settings.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Claim UI: all enrollment input happens here, never on the
            // terminal (D23, D31, Doc 2 §3.2).
            path:      '/claim',
            name:      'claim',
            component: () => import('@/views/enrollment/Claim.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

/** The sign-in page, coming back to `target` (default: the current page). */
export const loginRoute = (target?: string) => ({
    name:  'login',
    query: { redirect: target ?? router.currentRoute.value.fullPath },
})

/** A session that ended mid-page (a 401): the sign-in page. */
export const toLogin = (): void => {
    if (router.currentRoute.value.name === 'login') return

    void router.push(loginRoute())
}

/**
 * Where a sign-in may return to: an in-app path only. Anything else — another
 * origin (`//host`, `/\\host`, a scheme), a path no route matches, or a page
 * of the signed-out side — falls back to the fleet.
 */
export const safeRedirect = (value: unknown): string | undefined => {
    if (typeof value !== 'string' || !value.startsWith('/')) return undefined
    if (value.startsWith('//') || value.includes('\\')) return undefined
    if (/^[a-z][a-z0-9+.-]*:/i.test(value) || value.includes('://')) return undefined

    const resolved = router.resolve(value)
    if (!resolved.matched.length || resolved.meta.requiresAuth === false) return undefined

    return resolved.fullPath
}

router.beforeEach(async (to) => {
    const appStore = useAppStore()

    if (to.meta.requiresAuth === false) return true

    // The role is known before the first guarded view resolves, so an
    // operator never sees an admin page flash in (main.ts mounts after this).
    await appStore.loadSession()

    // Signed out: the sign-in page, which comes back to this page — the QR's
    // /claim?code=… included.
    if (!appStore.isLoggedIn()) return loginRoute(to.fullPath)

    if (to.meta.adminOnly && !appStore.isAdmin) return { name: 'fleet' }

    return true
})

export default router
